import React, { useEffect, useMemo } from 'react';
import './ProductDescriptionBlock.css';

/**
 * ProductDescriptionBlock
 * 
 * Displays admin-provided product descriptions with professional formatting.
 * Supports:
 * - Simple paragraphs
 * - Feature lists (bullet points prefixed with • or -)
 * - Key highlights (lines enclosed in ** or wrapped in <strong>)
 * - Responsive layout without inventing facts
 * 
 * Props:
 *   description (string): Raw text from admin (can include • or - for features)
 *   title (string, optional): Section title (default: "About This Product")
 *   highlightColor (string, optional): Color for highlights (default: "#111")
 */
const ProductDescriptionBlock = ({ 
  description, 
  title = 'About This Product',
  highlightColor = '#111'
}) => {
  
  // Parse description into structured sections
  const parsedContent = useMemo(() => {
    if (!description || typeof description !== 'string') {
      return { intro: '', features: [], highlights: [] };
    }

    const lines = description.split('\n').map(l => l.trim()).filter(Boolean);
    const result = { intro: '', features: [], highlights: [] };
    let introLines = [];

    lines.forEach((line) => {
      // Detect bullet points (features)
      if (line.match(/^[•-]\s+/)) {
        const featureText = line.replace(/^[•-]\s+/, '').trim();
        result.features.push(featureText);
      }
      // Detect highlights (**text** or <strong>)
      else if (line.includes('**') || line.includes('<strong>')) {
        const cleaned = line
          .replace(/\*\*/g, '')
          .replace(/<\/?strong>/g, '')
          .trim();
        result.highlights.push(cleaned);
      }
      // Accumulate regular text as intro
      else {
        introLines.push(line);
      }
    });

    result.intro = introLines.join(' ');
    return result;
  }, [description]);

  useEffect(() => {
    if (!description) return;
    console.debug('[ProductDescriptionBlock] rendering product description', {
      title,
      descriptionLength: description.length,
      introLength: parsedContent.intro.length,
      featureCount: parsedContent.features.length,
      highlightCount: parsedContent.highlights.length,
    });
  }, [title, description, parsedContent]);

  // If no description provided
  if (!description) {
    return null;
  }

  return (
    <div className="product-description-block">
      <h2 className="product-description-title">{title}</h2>

      {/* Intro paragraph */}
      {parsedContent.intro && (
        <p className="product-description-intro">
          {parsedContent.intro}
        </p>
      )}

      {/* Key Features Grid */}
      {parsedContent.features.length > 0 && (
        <div className="product-description-features">
          <h3 className="product-description-features-title">Key Features</h3>
          <ul className="product-description-features-list">
            {parsedContent.features.map((feature, idx) => (
              <li key={idx} className="product-description-feature-item">
                <span className="product-description-feature-icon">✓</span>
                <span className="product-description-feature-text">
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Highlights Section */}
      {parsedContent.highlights.length > 0 && (
        <div className="product-description-highlights">
          {parsedContent.highlights.map((highlight, idx) => (
            <div 
              key={idx} 
              className="product-description-highlight-item"
              style={{ '--highlight-color': highlightColor }}
            >
              <svg 
                className="product-description-highlight-icon" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 8 12 12 16 16" />
              </svg>
              <span>{highlight}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductDescriptionBlock;
